import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  Plane, ArrowRight, AlertCircle, Loader2, Minus, Plus, Calendar,
  Users, Baby, BadgePercent,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { handoffOfflineDealBooking } from '../../utils/offlineBookingHandoff';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtINR = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n || 0);
const formatDate = (yyyyMmDd) => {
  try {
    return format(parseISO(yyyyMmDd), 'EEE, d MMM yyyy');
  } catch {
    return yyyyMmDd;
  }
};

const Stepper = ({ label, sub, value, min = 0, max = 9, onChange, testIdBase, Icon }) => (
  <div className="flex items-center justify-between py-2.5">
    <div className="flex items-center gap-2.5">
      {Icon && (
        <div className="w-8 h-8 rounded-lg bg-[#F5FBFB] border border-[#659A9B]/20 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-[#1B375B]" />
        </div>
      )}
      <div>
        <div className="text-sm font-semibold text-[#1B375B]">{label}</div>
        {sub && <div className="text-[11px] text-gray-500">{sub}</div>}
      </div>
    </div>
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        className="w-8 h-8 rounded-lg border border-[#659A9B]/35 bg-white text-[#1B375B] hover:bg-[#F5FBFB] disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
        data-testid={`${testIdBase}-minus`}
        aria-label={`Decrease ${label}`}
      >
        <Minus className="w-3.5 h-3.5" />
      </button>
      <span
        className="w-7 text-center text-sm font-semibold text-[#1B375B] tabular-nums"
        data-testid={`${testIdBase}-value`}
      >
        {value}
      </span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        className="w-8 h-8 rounded-lg border border-[#659A9B]/35 bg-white text-[#1B375B] hover:bg-[#F5FBFB] disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
        data-testid={`${testIdBase}-plus`}
        aria-label={`Increase ${label}`}
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  </div>
);

/**
 * PaxQuickPicker — bottom-sheet/dialog that turns a "Book Now" tap on a
 * Special Deal card into a 2-tap booking entry: pick date(s) + travellers →
 * straight to /booking.
 *
 * Props
 *  - deal:           sanitised card payload from /api/public/offline-deals
 *  - preselectedDate (optional): YYYY-MM-DD pre-selected from a date chip
 *  - open / onOpenChange: controlled by parent
 */
const PaxQuickPicker = ({ deal, preselectedDate, open, onOpenChange }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [fullDeal, setFullDeal] = useState(null); // null = not loaded
  const [loadError, setLoadError] = useState(null);
  const [onwardDate, setOnwardDate] = useState(preselectedDate || '');
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);

  const isRoundTrip = deal?.ticket_type === 'roundtrip';

  // Fetch full upcoming dates list once when the modal opens
  useEffect(() => {
    if (!open || !deal?.id) return;
    let cancelled = false;
    setLoadError(null);
    setFullDeal(null);
    axios
      .get(`${API}/api/public/offline-deals/${deal.id}`, { timeout: 8000 })
      .then((resp) => {
        if (cancelled) return;
        setFullDeal(resp.data);
        const dates = resp.data?.upcoming_dates || [];
        if (!onwardDate && dates.length) {
          setOnwardDate(preselectedDate || dates[0].date);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(e?.response?.data?.detail || e.message || 'Failed to load deal');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deal?.id]);

  // Reset on modal close
  useEffect(() => {
    if (!open) {
      setAdults(1);
      setChildren(0);
      setInfants(0);
      setLoadError(null);
    }
  }, [open]);

  const upcomingDates = fullDeal?.upcoming_dates || [];

  const onwardRow = useMemo(
    () => upcomingDates.find((d) => d.date === onwardDate),
    [upcomingDates, onwardDate],
  );
  // RT pairing is strict: the return leg comes from the SAME inventory row.
  // The traveller cannot independently pick a return date — eliminates the
  // mismatch-dates bug class.
  const returnRow = isRoundTrip && onwardRow?.return_date
    ? {
        date: onwardRow.return_date,
        available_seats: onwardRow.available_seats,  // same pool
        status: 'active',
      }
    : null;

  // Live cap: cycle inventory is shared across both legs by definition.
  const paxTotal = adults + children;
  const limitingLegSeats = onwardRow?.available_seats || 0;

  // Cap adults dynamically based on the limiting leg minus children already chosen
  useEffect(() => {
    if (adults + children > limitingLegSeats && limitingLegSeats > 0) {
      // Trim from adults first, then children
      const overflow = adults + children - limitingLegSeats;
      if (children >= overflow) setChildren((c) => c - overflow);
      else {
        setChildren(0);
        setAdults((a) => Math.max(1, a - (overflow - children)));
      }
    }
  }, [limitingLegSeats, adults, children]);

  const adultMax = Math.max(1, Math.min(9, limitingLegSeats - children));
  const childMax = Math.max(0, Math.min(6, limitingLegSeats - adults));
  const infantMax = Math.min(adults, 4);

  // Fare math
  const adultFare = fullDeal?.lowest_fare?.adult || deal?.lowest_fare?.adult || 0;
  const childFare = fullDeal?.lowest_fare?.child || deal?.lowest_fare?.child || 0;
  const infantFare = fullDeal?.lowest_fare?.infant || deal?.lowest_fare?.infant || 0;
  const legMultiplier = isRoundTrip ? 2 : 1;
  const subtotal =
    (adultFare * adults + childFare * children + infantFare * infants) * legMultiplier;

  const continueDisabled =
    !onwardRow ||
    (isRoundTrip && !onwardRow.return_date) ||
    paxTotal > limitingLegSeats ||
    paxTotal < 1 ||
    !!loadError;

  const handleContinue = () => {
    // Strict RT pairing: the cycle row carries its own return_date. We pass
    // the onward cycle row and `handoffOfflineDealBooking` builds both legs
    // from it.
    handoffOfflineDealBooking({
      deal: fullDeal || deal,
      onwardDateRow: onwardRow,
      adults,
      children,
      infants,
      navigate,
    });
    onOpenChange?.(false);
  };

  if (!deal) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md md:max-w-lg p-0 gap-0 bg-white border-[#659A9B]/20 max-h-[92vh] overflow-y-auto"
        data-testid="pax-quick-picker"
      >
        {/* Header */}
        <DialogHeader className="px-5 pt-5 pb-3 bg-gradient-to-br from-[#F5FBFB] to-white border-b border-[#659A9B]/15">
          <div className="flex items-center gap-2 mb-1">
            <BadgePercent className="w-4 h-4 text-[#659A9B]" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-[#335E77]">ACF Special Deal</span>
          </div>
          <DialogTitle className="text-lg font-bold text-[#1B375B] flex items-center gap-2 flex-wrap">
            <span>{deal.route?.from_airport}</span>
            <ArrowRight className="w-4 h-4 text-[#659A9B]" />
            <span>{deal.route?.to_airport}</span>
            <span className="text-[11px] font-medium text-gray-500 ml-1">
              · {deal.airline_name} · {deal.flight_number}
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-600 mt-1">
            {isRoundTrip ? 'Round-trip' : 'One-way'} · {deal.departure_time} — {deal.arrival_time}
            {deal.duration ? ` · ${deal.duration}` : ''}
          </DialogDescription>
        </DialogHeader>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Loading */}
          {!fullDeal && !loadError && (
            <div className="py-8 text-center text-sm text-gray-500" data-testid="picker-loading">
              <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin text-[#659A9B]" />
              Loading availability…
            </div>
          )}
          {loadError && (
            <div className="p-3 bg-[#FFF8EC] border border-[#F5A623]/30 rounded-lg text-xs text-[#92400e]" data-testid="picker-error">
              <AlertCircle className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
              {loadError}
            </div>
          )}

          {fullDeal && (
            <>
              {/* Onward / Cycle date */}
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-[#659A9B]" />
                  {isRoundTrip ? 'Travel cycle (onward ⇆ return)' : 'Departure date'}
                </label>
                <select
                  value={onwardDate}
                  onChange={(e) => setOnwardDate(e.target.value)}
                  className="w-full px-3 py-2.5 bg-white border border-[#659A9B]/30 text-sm font-medium text-[#1B375B] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#659A9B]/40 focus:border-[#659A9B]"
                  data-testid="picker-onward-date"
                >
                  {upcomingDates.map((d) => {
                    const onLabel = formatDate(d.date);
                    const reLabel = d.return_date ? formatDate(d.return_date) : '';
                    const occ = d.occasion_label_name ? ` · ${d.occasion_label_name}` : '';
                    return (
                      <option key={d.date} value={d.date}>
                        {isRoundTrip && reLabel ? `${onLabel} ⇆ ${reLabel}` : onLabel}
                        {' '}· {d.available_seats} seat{d.available_seats === 1 ? '' : 's'}{occ}
                      </option>
                    );
                  })}
                </select>
                {isRoundTrip && onwardRow && !onwardRow.return_date && (
                  <p className="text-[11px] text-[#92400e] mt-1" data-testid="picker-pairing-warning">
                    This cycle is awaiting admin pairing — booking temporarily unavailable.
                  </p>
                )}
                {isRoundTrip && onwardRow?.occasion_label_name && (
                  <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#335E77]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#659A9B]" />
                    Occasion: {onwardRow.occasion_label_name}
                  </div>
                )}
              </div>

              {/* Travellers */}
              <div className="pt-1">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5 text-[#659A9B]" />
                  Travellers
                  {limitingLegSeats > 0 && (
                    <span className="text-gray-400 normal-case font-normal">· max {limitingLegSeats} on selected date{isRoundTrip ? 's' : ''}</span>
                  )}
                </div>
                <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl px-3">
                  <Stepper label="Adults" sub="12+ yrs" value={adults} min={1} max={adultMax} onChange={setAdults} testIdBase="picker-adults" Icon={Users} />
                  <Stepper label="Children" sub="2 – 12 yrs" value={children} max={childMax} onChange={setChildren} testIdBase="picker-children" Icon={Users} />
                  <Stepper label="Infants" sub="Under 2 yrs · lap-held" value={infants} max={infantMax} onChange={setInfants} testIdBase="picker-infants" Icon={Baby} />
                </div>
                {paxTotal === limitingLegSeats && limitingLegSeats > 0 && (
                  <div className="flex items-center gap-1.5 mt-2 text-[11px] text-[#92400e] font-medium" data-testid="picker-max-warning">
                    <AlertCircle className="w-3.5 h-3.5" />
                    All available seats selected on the limiting date
                  </div>
                )}
              </div>

              {/* Fare summary */}
              <div className="p-3 rounded-xl bg-[#F5FBFB] border border-[#659A9B]/20" data-testid="picker-fare-summary">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Fare summary</div>
                <div className="space-y-1 text-xs text-gray-700">
                  {adults > 0 && (
                    <div className="flex justify-between"><span>{adults} × Adult{isRoundTrip ? ' (RT)' : ''}</span><span>₹{fmtINR(adultFare * adults * legMultiplier)}</span></div>
                  )}
                  {children > 0 && (
                    <div className="flex justify-between"><span>{children} × Child{isRoundTrip ? ' (RT)' : ''}</span><span>₹{fmtINR(childFare * children * legMultiplier)}</span></div>
                  )}
                  {infants > 0 && (
                    <div className="flex justify-between"><span>{infants} × Infant{isRoundTrip ? ' (RT)' : ''}</span><span>₹{fmtINR(infantFare * infants * legMultiplier)}</span></div>
                  )}
                </div>
                <div className="flex justify-between items-baseline mt-2 pt-2 border-t border-[#659A9B]/15">
                  <span className="text-sm font-semibold text-[#1B375B]">Subtotal</span>
                  <span className="text-lg font-bold text-[#1B375B]">₹{fmtINR(subtotal)}</span>
                </div>
                <p className="text-[10px] text-gray-500 mt-1">Final total including airline taxes, GST and convenience fee will be shown on the booking page.</p>
              </div>
            </>
          )}
        </div>

        {/* Footer CTA */}
        <div className="px-5 pb-5 pt-1 bg-white sticky bottom-0">
          <button
            type="button"
            onClick={handleContinue}
            disabled={continueDisabled || !fullDeal}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#1B375B] to-[#335E77] text-white font-semibold rounded-xl shadow-md hover:shadow-lg hover:from-[#0f2742] hover:to-[#1B375B] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="picker-continue-btn"
          >
            <Plane className="w-4 h-4" />
            {isAuthenticated ? 'Continue to Book' : 'Continue to Book'}
            <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-[10px] text-center text-gray-500 mt-2">
            Limited inventory — fares may change once these seats sell out.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaxQuickPicker;
